import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import useAppStore from '../stores/appStore';

const getPersistedLanguage = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('share-pen-app-storage') || '{}');
    return stored?.state?.language || 'en';
  } catch {
    return 'en';
  }
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', selectorLabel: 'Language' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr', selectorLabel: 'Idioma' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr', selectorLabel: 'Langue' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr', selectorLabel: 'Sprache' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr', selectorLabel: 'Idioma' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr', selectorLabel: 'Язык' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr', selectorLabel: '语言' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr', selectorLabel: '言語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', dir: 'ltr', selectorLabel: '언어' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', selectorLabel: 'भाषा' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', selectorLabel: 'اللغة' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', dir: 'rtl', selectorLabel: 'שפה' },
];

export const RTL_LANGUAGES = ['ar', 'he'];
export const normalizeLanguageCode = (lang) => (lang || 'en').split('-')[0].toLowerCase();
export const isRtlLanguage = (lang) => RTL_LANGUAGES.includes(normalizeLanguageCode(lang));
export const getLanguageDir = (lang) => isRtlLanguage(lang) ? 'rtl' : 'ltr';

export const changeLanguage = async (lang) => {
  await i18n.changeLanguage(lang);
  document.documentElement.dir = getLanguageDir(lang);
  document.documentElement.lang = lang;
  useAppStore.setState({ language: lang });
};

const loadLocale = async (lang) => {
  const normalizedLang = normalizeLanguageCode(lang);
  if (normalizedLang === 'en') return en;
  const module = await import(`./locales/${normalizedLang}.json`);
  return module.default;
};

export const loadAndChangeLanguage = async (lang) => {
  if (!i18n.hasResourceBundle(lang, 'translation')) {
    const resources = await loadLocale(lang);
    i18n.addResourceBundle(lang, 'translation', resources);
  }
  await changeLanguage(lang);
};

const initialLang = getPersistedLanguage();

i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

document.documentElement.dir = getLanguageDir(initialLang);
document.documentElement.lang = initialLang;

if (initialLang !== 'en') {
  loadAndChangeLanguage(initialLang);
}

export default i18n;
